import { useEffect, useState } from 'react'

/**
 * Desktop-only WhatsApp QR. Renders the SAME `wa.me` deep link the mobile
 * button uses, so scanning it opens WhatsApp on the user's phone with the plan
 * request already typed — they just hit send.
 *
 * WHY THIS EXISTS: a desktop visitor cannot complete the handoff. `sms:` has no
 * handler and `wa.me` routes to WhatsApp Web, which demands a phone-scan of its
 * own QR before it will send. Measured over both CTA eras (US, dogfood
 * excluded): desktop 56 impressions -> 6 clicks -> **0 redemptions**, ever,
 * against mobile's 419 -> 159 -> 74. The QR is the shortest path from "the
 * screen that cannot finish this" to "the device that can".
 *
 * The `qrcode` package is loaded with a DYNAMIC import on purpose: ~80% of
 * traffic is mobile and must not download an encoder it will never render. The
 * import happens inside the effect, so it is only fetched once a desktop
 * visitor actually reaches the card.
 *
 * FAILS OPEN: if the encoder fails to load or throws, `dataUrl` stays null and
 * the caller still shows its text link — a desktop user is never left with no
 * action at all.
 */
export function WhatsAppQr({ href, caption }: { href: string; caption: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const QRCode = await import('qrcode')
        const url = await QRCode.toDataURL(href, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 320, // rendered at 160 CSS px → crisp on 2x displays
          color: { dark: '#111827', light: '#ffffff' },
        })
        if (!cancelled) setDataUrl(url)
      } catch {
        // Fail open — the caller's text link remains the action.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [href])

  if (!dataUrl) return null

  return (
    <div className="cta-card-qr">
      {/* Decorative: the caption below carries the instruction, and the same
          destination is reachable via the text link the caller renders. */}
      <img src={dataUrl} alt="" width={160} height={160} aria-hidden="true" />
      <p className="cta-card-fineprint cta-card-qr-caption">{caption}</p>
    </div>
  )
}
